/**
 * Compliance Management — hub page. Aggregates all 10 GST/tax compliance
 * workflows in one place with card-based navigation.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ShieldCheck, FileText, Repeat, FileCheck, ScrollText,
         ClipboardList, FileBarChart2, PackageCheck, Receipt, RefreshCw } from "lucide-react";

const TILES = [
  { title: "GST Reports (Live)",       desc: "12-report hub — GSTR-1/2B/3B/6/6A · e-Invoice · EWB · Ledgers · Filing status",
      to: "/app/reports/gst-reports", icon: ShieldCheck, colour: "text-emerald-500" },
  { title: "GSTR-1 · 3-Way Reco",      desc: "Reconcile sales register vs GSTR-1 vs e-Invoice · fix mismatches before filing",
      to: "/app/gstr1",               icon: FileText,    colour: "text-blue-500" },
  { title: "IMS · GSTR-2B Reco",       desc: "Accept / Reject / Pending on every 2B row · lock ITC before 20th",
      to: "/app/ims",                 icon: Repeat,      colour: "text-purple-500" },
  { title: "GSTR-3B",                  desc: "Auto-populated 3B with Rule 42 reversal · draft JV linked",
      to: "/app/gstr3b",              icon: FileCheck,   colour: "text-amber-500" },
  { title: "GSTR-6 / 6A",              desc: "ISD return · fetch inward supplies & distribute credit",
      to: "/app/compliance/gstr6",    icon: FileBarChart2, colour: "text-cyan-500" },
  { title: "GSTR-9",                   desc: "Annual return · reconcile books vs filed GSTR-1 + 3B for the year",
      to: "/app/compliance/gstr9",    icon: ClipboardList, colour: "text-indigo-500" },
  { title: "GSTR-9C",                  desc: "Annual reconciliation statement (audited turnover >= ₹5 Cr)",
      to: "/app/compliance/gstr9c",   icon: ScrollText,  colour: "text-rose-500" },
  { title: "ITC-04",                   desc: "Goods sent to job-worker & return within 1 yr (or 3 yr for capital)",
      to: "/app/compliance/itc-04",   icon: PackageCheck, colour: "text-orange-500" },
  { title: "RCM Register",             desc: "Ledger of every RCM liability + Sec 17(5) AI-flagged ineligible ITC",
      to: "/app/reports/rcm-register", icon: Receipt,    colour: "text-red-500" },
  { title: "Rule 37 Reversal",         desc: "Auto-reverse ITC where supplier hasn't been paid within 180 days",
      to: "/app/reports/rule37-reversal", icon: RefreshCw, colour: "text-yellow-500" },
];

export default function Compliance() {
  const nav = useNavigate();
  return (
    <div className="p-6 space-y-6" data-testid="compliance-hub">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <ShieldCheck className="w-8 h-8 text-emerald-500" />
          Compliance Management
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Every GST return, reconciliation, and statutory register in one place.
          Live from gst.gov.in through Zenith360-GST; IRN / E-Way Bill directly with NIC.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {TILES.map((t) => (
          <Card key={t.to}
                  className="cursor-pointer hover:border-primary transition-all"
                  onClick={() => nav(t.to)}
                  data-testid={`tile-${t.to.split("/").pop()}`}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <t.icon className={`w-5 h-5 ${t.colour}`} />
                {t.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground pt-0">{t.desc}</CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
